import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SitesDataService } from '@ffd/api-client';
import { InputComponent } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { eaglesoftPasswordModalMatchValidator, eaglesoftPasswordModalValidators } from './eaglesoft-password-modal.validators';

export interface EaglesoftPasswordModalData {
  siteId: string;
  /** Set by the replay path, where a write is held open and closing this dialog costs the change. */
  warnAboutDataLoss?: boolean;
}

/** `success: false` is a dismissal — the caller decides what that costs (no edit mode, a failed write). */
export interface EaglesoftPasswordModalResult {
  success: boolean;
}

@Component({
  selector: 'lib-eaglesoft-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, InputComponent, SvgIconComponent],
  templateUrl: './eaglesoft-password-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EaglesoftPasswordModalComponent {
  protected readonly dialogData: EaglesoftPasswordModalData = inject(DIALOG_DATA);
  private readonly dialogRef: DialogRef<EaglesoftPasswordModalResult> = inject(DialogRef);
  private readonly sitesDataService = inject(SitesDataService);

  protected readonly isSubmitting = signal(false);
  /**
   * The update forwards to the on-premise site synchronously, so it can time out (504). A gateway
   * timeout carries no parsed body for the global toast, so the failure is stated here.
   */
  protected readonly hasSubmitFailed = signal(false);
  protected readonly isPasswordVisible = signal(false);

  protected readonly passwordForm = new FormGroup(
    {
      password: new FormControl('', { validators: eaglesoftPasswordModalValidators, nonNullable: true }),
      // Only required + match — the full format rules are already enforced on `password`,
      // so re-running them here would mask "passwords don't match" behind an unrelated
      // format error whenever a mistyped confirmation also happens to break a format rule.
      confirmPassword: new FormControl('', { validators: Validators.required, nonNullable: true }),
    },
    { validators: eaglesoftPasswordModalMatchValidator },
  );

  protected onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  protected togglePasswordVisibility(): void {
    this.isPasswordVisible.set(!this.isPasswordVisible());
  }

  protected passwordErrorKey(control: 'password' | 'confirmPassword'): string | null {
    const formControl = this.passwordForm.controls[control];
    if (!formControl.touched || formControl.valid) {
      return null;
    }

    const errors = formControl.errors;
    if (errors?.['required']) return 'account_eaglesoft_password_error_required';
    if (errors?.['minlength']) return 'account_eaglesoft_password_error_length';

    const failedPatterns = errors?.['multiPattern'] as Record<string, true> | undefined;
    if (failedPatterns?.['uppercase']) return 'account_eaglesoft_password_error_uppercase';
    if (failedPatterns?.['lowercase']) return 'account_eaglesoft_password_error_lowercase';
    if (failedPatterns?.['digit']) return 'account_eaglesoft_password_error_digit';
    if (failedPatterns?.['specialChar']) return 'account_eaglesoft_password_error_special_char';
    if (failedPatterns?.['noSpaces']) return 'account_eaglesoft_password_error_no_spaces';

    return null;
  }

  protected isConfirmPasswordMismatched(): boolean {
    return (
      this.passwordForm.controls.confirmPassword.touched &&
      this.passwordForm.controls.confirmPassword.valid &&
      !!this.passwordForm.errors?.['passwordNoMatch']
    );
  }

  protected onSubmit(): void {
    this.passwordForm.markAllAsTouched();

    if (this.passwordForm.invalid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);
    this.hasSubmitFailed.set(false);
    this.sitesDataService
      .updateEaglesoftPassword(this.dialogData.siteId, { password: this.passwordForm.controls.password.value })
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.dialogRef.close({ success: true });
        },
        error: () => {
          this.isSubmitting.set(false);
          this.hasSubmitFailed.set(true);
        },
      });
  }
}
